import { useEffect } from 'react'

import { config } from '@skytech/config'
import Linkify from 'react-linkify'
import { useDispatch, useSelector } from 'react-redux'
import { CSSTransition, TransitionGroup } from 'react-transition-group'

import Button from '@/components/form/Button'
import Input from '@/components/form/Input'
import ReactSelect from '@/components/form/ReactSelect'
import { Translation } from '@/components/i18n/Translation'
import { LocalDate } from '@/components/LocalDate'
import { activatePopup } from '@/ducks/popupReducer'
import {
  getIsPostPending,
  getSelectedAlias,
  getSmsInputText,
  getSubscriptionOrder,
  sendDeviceOrderSms,
  sendSubscriptionOrderRemainderSms,
  setAlias,
  setSmsInputText,
} from '@/ducks/subscriptionOrderReducer'
import { useProfile } from '@/hooks/user'
import { SEC } from '@/util/formatters/dates'
import { isOrderOfType, SUBSCRIPTION_ORDER_TYPES, TERMINATION_TYPE } from '@/util/orders'

import ResendAuthMailPrompt from './ResendAuthMailPrompt'

import './subscription-order-communications.scss'

const SubscriptionOrderCommunications = () => {
  const isDevelopmentMode = config.ENVIRONMENT === 'dev'

  const dispatch = useDispatch()
  const aliases = useProfile()?.aliases

  const smsInputText = useSelector(getSmsInputText)
  const selectedAlias = useSelector(getSelectedAlias)
  const isPostPending = useSelector(getIsPostPending)
  const order = useSelector(getSubscriptionOrder)
  const { sms_log, code, contact_number, termination_type_id } = order

  const phoneNumber = code || contact_number

  useEffect(() => {
    // @ts-ignore
    !selectedAlias && aliases.length > 0 && dispatch(setAlias(aliases[0].id))
  }, [selectedAlias, aliases, dispatch])
  // @ts-ignore
  const options = aliases.map(alias => ({
    value: alias.id,
    label: alias.alias,
  }))

  return (
    // Show developer the section if no aliases but in development mode
    // @ts-ignore
    (aliases.length > 0 || isDevelopmentMode) && (
      <section>
        <div className="subscription-order-communications">
          <h3>
            <Translation id="Communications" />
          </h3>

          <form>
            {/* @ts-ignore */}
            {aliases.length > 1 && (
              <>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label>Sender</label>
                <ReactSelect
                  options={options}
                  value={selectedAlias}
                  // @ts-ignore
                  onChange={value => dispatch(setAlias(value))}
                />
              </>
            )}

            <Input
              label={<Translation id="New message" />}
              value={smsInputText}
              onChange={e => dispatch(setSmsInputText(e.target.value))}
              disabled={isPostPending}
            />

            <Button
              // @ts-ignore
              onClick={() => dispatch(sendDeviceOrderSms())}
              disabled={isPostPending || !phoneNumber || !smsInputText}
            >
              <Translation id="Send sms" />
            </Button>

            {(isOrderOfType(order, SUBSCRIPTION_ORDER_TYPES.TERMINATION) ||
              isOrderOfType(order, SUBSCRIPTION_ORDER_TYPES.PORTING)) && (
              <Button
                // @ts-ignore
                onClick={() => dispatch(sendSubscriptionOrderRemainderSms())}
                disabled={isPostPending || !phoneNumber}
              >
                <Translation id="Send reminder sms" />
              </Button>
            )}

            {isOrderOfType(order, SUBSCRIPTION_ORDER_TYPES.TERMINATION) &&
              [TERMINATION_TYPE.CANCELLATION, TERMINATION_TYPE.TRANSFER].includes(
                termination_type_id
              ) && (
                <Button
                  type="button"
                  onClick={() => dispatch(activatePopup(<ResendAuthMailPrompt />))}
                  disabled={isPostPending || !phoneNumber}
                >
                  <Translation id="Resend authorization mail" />
                </Button>
              )}
          </form>

          {/* TODO: refactor and use SmsLogItem component */}
          <TransitionGroup className="sms-log">
            {/* @ts-ignore */}
            {sms_log.map(sms => (
              <Sms key={sms.sent} {...sms} />
            ))}
          </TransitionGroup>
        </div>
      </section>
    )
  )
}

const Sms = ({ sender, message, sent }: any) => (
  <CSSTransition classNames="sms-wrapper" in={true} timeout={10 * SEC}>
    <div className="sms-wrapper">
      <div className="sender">{sender}</div>
      <div className="timestamp">
        <LocalDate date={sent} />
      </div>
      <div className="message">
        {/* @ts-ignore */}
        <Linkify properties={{ target: '_blank' }}>{message}</Linkify>
      </div>
    </div>
  </CSSTransition>
)

export default SubscriptionOrderCommunications
